"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { Search, FileText, Globe, Activity, FileBarChart } from "lucide-react";
import { GradientButton } from "@/components/ui/gradient-button";
import { InfoOverlay } from "@/components/HomePage/Layout/InfoOverlay";
import { SpringElement } from "@/components/ui/spring-element";
import { TextEffect } from "@/components/ui/text-effect";
import { Carousel, CarouselContent, CarouselIndicator, CarouselItem } from "@/components/ui/carousel";
import FAQ from "@/components/HomePage/Sections/FAQ";
import { ChromeBrowserSectionGlobalScroll } from "@/components/HomePage/Sections";
import { Footer } from "@/components/HomePage/Layout/Footer";
import { useHomePageFontSize } from "@/components/HomePage/hooks/useHomePageFontSize";
import { useLenis } from "@/hooks/useLenis";

const USE_CASES = [
  {
    id: "analytics",
    tab: "Data Analytics",
    title: "Data Analytics",
    description: "Make data-driven decisions",
    video: "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/use+case/Data+Analytics.webm",
  },
  {
    id: "career",
    tab: "Career Growth",
    title: "Career Growth",
    description: "Advance your career with AI-powered tools",
    video: "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/use+case/Career+Development.webm",
  },
  {
    id: "marketing",
    tab: "Marketing",
    title: "Marketing",
    description: "Supercharge your marketing campaigns",
    video: "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/use+case/Marketing+.webm",
  },
  {
    id: "study",
    tab: "Study",
    title: "Study",
    description: "Enhance your learning experience",
    video: "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/use+case/Study.webm",
  },
  {
    id: "productivity",
    tab: "Productivity",
    title: "Productivity",
    description: "Boost your daily productivity",
    video: "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/use+case/Productivity.webm",
  },
  {
    id: "daily-life",
    tab: "Daily Life",
    title: "Daily Life",
    description: "Simplify your everyday tasks",
    video: "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/use+case/Daily+Life.webm",
  },
];

export default function MobileHomePage() {
  const [isDownloadOverlayOpen, setIsDownloadOverlayOpen] = useState(false);
  const [backgroundReady, setBackgroundReady] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [overlayData, setOverlayData] = useState<{
    still: string;
    fellou: string;
    variant: "step" | "search" | "browser" | "computer" | "controllable" | "memory" | "menu";
  }>({ still: "", fellou: "", variant: "step" });
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [loadingVideos, setLoadingVideos] = useState<Record<string, boolean>>({});

  // Reset font-size for MobileHomePage to ensure proper Tailwind rendering and isolation
  useHomePageFontSize();

  // Initialize Lenis smooth scrolling
  useLenis({
    duration: 1.5,
    easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
  });

  // Set background ready immediately for static image
  useEffect(() => {
    setBackgroundReady(true);
  }, []);

  const handleDownloadClick = () => {
    setIsDownloadOverlayOpen(true);
  };

  // Track viewport size for overlay gradients
  useEffect(() => {
    const updateSize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const openInfo = (
    still: string,
    fellou: string,
    variant: "step" | "search" | "browser" | "computer" | "controllable" | "memory" | "menu"
  ) => {
    setOverlayData({ still, fellou, variant });
    setIsInfoOpen(true);
  };
  const closeInfo = () => setIsInfoOpen(false);
  const closeDownloadOverlay = () => setIsDownloadOverlayOpen(false);

  // Toggle compact header when scrolled away from very top
  useEffect(() => {
    const onScroll = () => setShowHeader(window.scrollY > 60);

    // Check immediately
    onScroll();

    // Add scroll listener with delay to coordinate with Lenis
    const timeoutId = setTimeout(() => {
      window.addEventListener("scroll", onScroll, { passive: true });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <>
      {/* Mobile Background - Static bg01.png */}
      <div className="fixed inset-0 bg-black z-0" />
      <div className="fixed inset-0 z-5 overflow-hidden">
        <Image src="/bg01.png" alt="Background" fill className="object-cover" quality={90} priority />
        <div className="absolute inset-0 bg-black/40" />
      </div>

      <div className="text-white relative z-10">
        {/* Hero Section - Adapted from ChromeBrowserSection */}
        <section className="px-4 sm:px-6 pt-32 pb-10 sm:pt-20 sm:pb-12 text-center">
          <div className="max-w-4xl mx-auto">
            {/* Fellou Logo Pill - sticky with framer-motion (no Tailwind opacity clash) */}
            <motion.div
              className="inline-flex flex-wrap items-center px-1 mb-8 bg-gray-400/20 backdrop-blur-sm rounded-full border border-gray-300/30 py-1  top-2"
              initial={{ opacity: 0, filter: "blur(4px)" }}
              animate={{ opacity: backgroundReady ? 1 : 0, filter: backgroundReady ? "blur(0px)" : "blur(4px)" }}
              transition={{ delay: backgroundReady ? 0.5 : 0 }}
              style={{ pointerEvents: "auto" }}
            >
              <motion.div
                className="flex items-center gap-3 px-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: backgroundReady ? 1 : 0 }}
                transition={{ delay: backgroundReady ? 0.7 : 0 }}
              >
                <div className="relative">
                  <Image
                    src="/favicon.ico"
                    alt="Fellou Logo"
                    width={32}
                    height={32}
                    className="w-8 h-8 relative z-10 drop-shadow-2xl"
                    unoptimized
                  />
                </div>
                <span className="text-2xl font-bold text-white">Fellou</span>
              </motion.div>
              <motion.div
                className="mr-1"
                initial={{ width: 0, opacity: 0, filter: "blur(4px)" }}
                animate={{
                  width: backgroundReady ? "auto" : 0,
                  opacity: backgroundReady ? 1 : 0,
                  filter: backgroundReady ? "blur(0px)" : "blur(4px)",
                }}
                transition={{ delay: backgroundReady ? 1.1 : 0 }}
              >
                <GradientButton onClick={() => openInfo("", "", "menu")} className="text-sm px-4 py-2 sm:px-6 sm:py-3">
                  Menu
                </GradientButton>
              </motion.div>
            </motion.div>

            {/* Main Title - with animations */}
            <h1 className="text-4xl sm:text-6xl leading-tight font-bold text-white mb-4">
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: backgroundReady ? 1 : 0 }}
                transition={{ delay: backgroundReady ? 0.8 : 0 }}
              >
                The W
                <SpringElement
                  className="text-4xl sm:text-6xl relative inline-block"
                  springClassName="stroke-white dark:stroke-white stroke-[2px]"
                  dragElastic={0.3}
                  springConfig={{ stiffness: 150, damping: 30 }}
                  springPathConfig={{
                    coilCount: 6,
                    amplitudeMin: 10,
                    amplitudeMax: 25,
                    curveRatioMin: 0.6,
                    curveRatioMax: 1.2,
                    bezierOffset: 12,
                  }}
                >
                  <span aria-label="O" className="block select-none">
                    🌎
                  </span>
                </SpringElement>
                rld's{" "}
              </motion.span>
              <motion.span
                initial={{ x: -200, opacity: 0, scale: 0.8, filter: "blur(4px)", skewX: 10 }}
                animate={{
                  x: backgroundReady ? 0 : -200,
                  opacity: backgroundReady ? 1 : 0,
                  scale: backgroundReady ? 1 : 0.8,
                  filter: backgroundReady ? "blur(0px)" : "blur(4px)",
                  skewX: backgroundReady ? 0 : 0,
                }}
                transition={{ delay: backgroundReady ? 1.1 : 0 }}
                className="inline-block"
              >
                First
              </motion.span>
              <br />
              <TextEffect
                className="flex justify-center font-serif"
                per="char"
                preset="fade-in-blur"
                delay={1.4}
                speedSegment={2}
                variants={{
                  item: {
                    hidden: {
                      opacity: 0,
                      filter: "blur(10px) brightness(0%)",
                      y: 30,
                      scale: 0.8,
                      rotateX: 15,
                    },
                    visible: {
                      opacity: 1,
                      filter: "blur(0px) brightness(100%)",
                      y: 0,
                      scale: 1,
                      rotateX: 0,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut",
                      },
                    },
                    exit: {
                      opacity: 0,
                      filter: "blur(10px) brightness(0%)",
                      y: -30,
                      scale: 0.8,
                      rotateX: -15,
                    },
                  },
                }}
              >
                Agentic Browser
              </TextEffect>
            </h1>

            {/* Description */}
            <motion.p
              className="text-sm  text-white/60 max-w-2xl mx-auto mb-6 sm:mb-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: backgroundReady ? 1 : 0, y: backgroundReady ? 0 : 20 }}
              transition={{ delay: backgroundReady ? 2.0 : 0, duration: 0.6 }}
            >
              Go beyond browsing with Fellou AI Browser that takes automated web action
            </motion.p>
          </div>
        </section>

        {/* Global Scroll Chrome Browser Demo (30vh timeline) */}
        <ChromeBrowserSectionGlobalScroll onDownloadClick={handleDownloadClick} onShowHeaderChange={setShowHeader} />

        {/* User Cases Section - Adapted from UserCaseSection */}
        <section className="px-4 sm:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Title */}
            <div className="text-center mb-4">
              <h2 className="text-2xl text-white  font-bold">What Fellou Can Do For You</h2>
              <p className="mt-2 text-sm text-white/60">
                <a href="/use-cases" className="hover:text-white/80 transition-colors">
                  Discover more →
                </a>
              </p>
            </div>

            {/* Video Carousel */}
            <div className="relative w-full max-w-4xl mx-auto pb-6">
              <Carousel>
                <CarouselContent>
                  {USE_CASES.map((useCase, index) => (
                    <CarouselItem key={useCase.id}>
                      <div className="pb-4 mx-2">
                        {/* Use Case Info */}
                        <div className="text-center mb-2">
                          <h3 className="text-lg font-semibold text-white">{useCase.title}</h3>
                        </div>
                        <div className="relative w-full max-h-[45vh] sm:max-h-[400px] min-h-[200px] bg-transparent rounded-lg">
                          {loadingVideos[useCase.id] !== false && (
                            <div className="absolute inset-0 bg-gradient-to-br from-gray-800/60 via-gray-700/40 to-gray-800/60 rounded-lg flex flex-col items-center justify-center backdrop-blur-sm border border-white/10 z-10">
                              {/* Video placeholder skeleton */}
                              <div className="w-full h-full absolute inset-0 rounded-lg overflow-hidden">
                                {/* Animated shimmer effect */}
                                <div
                                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"
                                  style={{
                                    backgroundSize: "200% 100%",
                                    animation: "shimmer 2s infinite linear",
                                  }}
                                ></div>
                                {/* Grid pattern overlay */}
                                <div
                                  className="absolute inset-0 opacity-20"
                                  style={{
                                    backgroundImage: `
                                         radial-gradient(circle at 2px 2px, rgba(255,255,255,0.3) 1px, transparent 0)
                                       `,
                                    backgroundSize: "20px 20px",
                                  }}
                                ></div>
                              </div>

                              {/* Play button icon */}
                              <div className="relative z-10 bg-white/10 backdrop-blur-sm rounded-full p-4 border border-white/20 animate-pulse">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-white/70">
                                  <path d="M8 5v14l11-7z" fill="currentColor" />
                                </svg>
                              </div>

                              {/* Loading text */}
                              <div className="relative z-10 mt-3 text-white/60 text-sm font-medium animate-pulse">
                                Loading {useCase.title}...
                              </div>
                            </div>
                          )}
                          <video
                            autoPlay
                            muted
                            loop
                            playsInline
                            className="w-full h-full object-contain rounded-lg"
                            onLoadStart={() => {
                              setLoadingVideos((prev) => ({ ...prev, [useCase.id]: true }));
                            }}
                            onCanPlay={() => {
                              setLoadingVideos((prev) => ({ ...prev, [useCase.id]: false }));
                            }}
                            onError={() => {
                              setLoadingVideos((prev) => ({ ...prev, [useCase.id]: false }));
                            }}
                          >
                            <source src={useCase.video} type="video/webm" />
                          </video>
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselIndicator />
              </Carousel>
            </div>
          </div>
        </section>

        {/* Deep Action Section */}
        <section className="px-4 sm:px-6 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4 text-white">Automated Complex Web Tasks with One Prompt</h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8 leading-relaxed">
              Automatically execute cross-app workflows through natural language descriptions
            </p>

            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full max-h-[45vh] sm:max-h-[400px] object-contain rounded-lg"
            >
              <source
                src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Deep+action.webm"
                type="video/webm"
              />
            </video>
          </div>
        </section>

        {/* Step List Section */}
        <section className="px-4 sm:px-6 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4 text-white">From Intent to Action Plan</h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8 leading-relaxed">
              Fellou analyzes your goal to create a detailed, step-by-step action plan at first. You can review, approve
              and edit this plan before execution.
            </p>

            <div className="mb-8">
              <GradientButton
                className="px-5 py-3"
                onClick={() => openInfo("Still typing long prompts?", "Fellou plans first, acts second.", "step")}
              >
                Know More
              </GradientButton>
            </div>

            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full max-h-[45vh] sm:max-h-[400px] object-contain rounded-lg"
            >
              <source
                src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Step+List.webm"
                type="video/webm"
              />
            </video>
          </div>
        </section>

        {/* Deep Search Section */}
        <section className="px-4 sm:px-6 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4 text-white">
              Automate Multi-Source Research into Traceable Reports
            </h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8 leading-relaxed">
              Fellou automates deep research across the entire internet, including logged-in accounts on platforms like
              Reddit, generating personalized and in-depth reports with verifiable sources
            </p>

            <div className="mb-8">
              <GradientButton
                className="px-5 py-3"
                onClick={() => openInfo("Still opening 10 tabs?", "Fellou searches deep, finds fast.", "search")}
              >
                Know More
              </GradientButton>
            </div>

            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full max-h-[45vh] sm:max-h-[400px] object-contain rounded-lg"
            >
              <source
                src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Deep+search.webm"
                type="video/webm"
              />
            </video>
          </div>
        </section>

        {/* Browser Use Section */}
        <section className="px-4 sm:px-6 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4 text-white">End-To-End Web Automation</h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8 leading-relaxed">
              Browser Use gives our Agentic AI Browser power to automate entire workflows. From complex data scraping to
              form filling, it handles every step from a single command, delivering complete, start-to-finish
              automation.
            </p>

            <div className="mb-8">
              <GradientButton
                className="px-5 py-3"
                onClick={() =>
                  openInfo("Still clicking through the web yourself?", "Fellou acts for you, like a pro.", "browser")
                }
              >
                Know More
              </GradientButton>
            </div>

            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full max-h-[45vh] sm:max-h-[400px] object-contain rounded-lg"
            >
              <source
                src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Browse+USE.webm"
                type="video/webm"
              />
            </video>
          </div>
        </section>

        {/* Computer Use Section */}
        <section className="px-4 sm:px-6 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4 text-white">Handles Desktop Files, Zero Effort</h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8 leading-relaxed">
              Computer Use transforms Fellou into a true system-level agent. Grant this AI Browser permission to operate
              local apps and manage files, giving you full control over a truly agentic workflow.
            </p>

            <div className="mb-8">
              <GradientButton
                className="px-5 py-3"
                onClick={() =>
                  openInfo("Still switching apps on AI's orders?", "Fellou runs the whole system easily.", "computer")
                }
              >
                Know More
              </GradientButton>
            </div>

            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full max-h-[45vh] sm:max-h-[400px] object-contain rounded-lg"
            >
              <source
                src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Computer+USE.webm"
                type="video/webm"
              />
            </video>
          </div>
        </section>

        {/* Controllable Section */}
        <section className="px-4 sm:px-6 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4 text-white">Intervene at Any Step of AI Workflows in Real Time</h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8 leading-relaxed">
              Fellou never works in a black box. For every task, it shows you its entire plan, step by step. You have
              the power to edit, approve, or step in at any moment, ensuring the AI browser always does exactly what you
              want.
            </p>

            <div className="mb-8">
              <GradientButton
                className="px-5 py-3"
                onClick={() =>
                  openInfo(
                    "Still can't step in when AI messes up?",
                    "Fellou gives you real-time control.",
                    "controllable"
                  )
                }
              >
                Know More
              </GradientButton>
            </div>

            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full max-h-[45vh] sm:max-h-[400px] object-contain rounded-lg"
            >
              <source
                src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Controllable+workflow.webm"
                type="video/webm"
              />
            </video>
          </div>
        </section>

        {/* Agentic Memory Section */}
        <section className="px-4 sm:px-6 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4 text-white">Agentic Memory Learns You, Knows You</h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8 leading-relaxed">
              Agentic Memory securely learns from your browser history and notes to understand your context. Allowing
              you to instantly recall past information and get answers about your previous work just by asking—no more
              searching.
            </p>

            <div className="mb-8">
              <GradientButton
                className="px-5 py-3"
                onClick={() =>
                  openInfo("Still manually feeding context to AI?", "Fellou remembers everything.", "memory")
                }
              >
                Know More
              </GradientButton>
            </div>

            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full max-h-[45vh] sm:max-h-[400px] object-contain rounded-lg"
            >
              <source
                src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Agentic+Memory.webm"
                type="video/webm"
              />
            </video>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQ />

        {/* Stories Section */}
        <section className="px-4 sm:px-6 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Stories That Inspire</h2>
            <p className="text-base text-white/80 mb-8 leading-relaxed">
              Unleash productivity, redefine your browsing experience.
            </p>

            {/* Mobile-optimized testimonial cards */}
            <div className="space-y-4">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-left">
                <p className="text-white/90 mb-3 text-sm leading-relaxed">
                  "Fellou didn't just beat the competition, it crushed them. Most accurate, clearest reports, deepest
                  insights. Easiest to read And it's 3.1x faster than OpenAI."
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gray-500"></div>
                    <div>
                      <p className="text-white font-medium text-xs">Guri Saroy</p>
                      <p className="text-white/60 text-xs">@HeyGurisaroy</p>
                    </div>
                  </div>
                  <div className="w-4 h-4">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="text-white/60">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-left">
                <p className="text-white/90 mb-3 text-sm leading-relaxed">
                  "Chrome was for browsing. Fellou is for doing it. Manage the Internet so you can manage your life. The
                  future is not the search, it is an exploration of action."
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gray-500"></div>
                    <div>
                      <p className="text-white font-medium text-xs">Filipe | IA</p>
                      <p className="text-white/60 text-xs">@filicroval</p>
                    </div>
                  </div>
                  <div className="w-4 h-4">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="text-white/60">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-left">
                <p className="text-white/90 mb-3 text-sm leading-relaxed">"This is the future of web browsing."</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gray-500"></div>
                    <div>
                      <p className="text-white font-medium text-xs">MARLON</p>
                      <p className="text-white/60 text-xs">@MarlonNFTs</p>
                    </div>
                  </div>
                  <div className="w-4 h-4">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="text-white/60">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="px-4 sm:px-6 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              Bring a digital companion to every person, on every device.
            </h2>
            <p className="text-base text-white/80 mb-8 leading-relaxed">
              Empowering humanity with intelligent productivity
            </p>
          </div>
        </section>

        {/* Footer */}
        <Footer onDownloadClick={handleDownloadClick} />

        {/* Info Overlay */}
        <AnimatePresence>
          {isInfoOpen && (
            <InfoOverlay
              close={closeInfo}
              size={size}
              still={overlayData.still}
              fellou={overlayData.fellou}
              variant={overlayData.variant}
            />
          )}
        </AnimatePresence>

        {/* Menu overlay unified into InfoOverlay via variant="menu" */}
      </div>

      {/* Download Overlay - outside main container like desktop version */}
      <AnimatePresence>
        {isDownloadOverlayOpen && <InfoOverlay close={closeDownloadOverlay} size={size} variant="download" />}
      </AnimatePresence>

      <style jsx global>{`
        /* Ensure sticky positioning works correctly - mobile homepage isolation */
        html,
        body {
          overflow-x: visible !important;
          overflow-y: visible !important;
          overflow: visible !important;
        }

        /* Remove any container styles that might interfere with sticky positioning */
        .fellou-layout-content {
          overflow: visible !important;
        }

        .overlay-root {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          /* Ensure consistent mobile viewport sizing */
          height: 100dvh;
          width: 100vw;
          overflow: hidden;
          z-index: 1000;
        }

        .overlay-content {
          background: rgb(147, 51, 234, 0.2);
          backdrop-filter: blur(3px);
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          /* Normalize height to dynamic viewport */
          height: 100%;
          min-height: 100dvh;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
          will-change: opacity;
        }

        .download-confirmation {
          width: 75%;
          max-width: 600px;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 30px;
          will-change: transform;
          /* Prevent content from making the overlay appear taller */
          max-height: calc(100dvh - 64px);
          overflow-y: auto;
        }

        .download-confirmation header {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 5px;
          text-align: center;
        }

        .download-confirmation h2 {
          color: #f5f5f5;
          margin: 0;
        }

        .download-confirmation p {
          color: #f5f5f5;
          margin: 0;
        }

        .controls {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 10px;
          width: 100%;
        }

        .platform-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 24px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: #f5f5f5;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          width: 100%;
          max-width: 300px;
          justify-content: center;
        }

        .platform-btn:hover {
          background: rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
        }

        .platform-btn-disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: rgba(255, 255, 255, 0.05);
        }

        .platform-btn-disabled:hover {
          transform: none;
          background: rgba(255, 255, 255, 0.05);
        }

        .platform-icon {
          width: 24px;
          height: 24px;
          flex-shrink: 0;
        }

        button.cancel {
          background: none;
          color: #f5f5f5;
          border: none;
          padding: 10px 20px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s;
        }

        button.cancel:hover {
          opacity: 0.7;
        }

        .windows-btn-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
        }

        .download-title {
          font-size: 1.5rem;
          line-height: 2rem;
        }

        .coming-soon-text {
          font-size: 0.75rem;
          line-height: 1rem;
          background: linear-gradient(to right, #c084fc, #9333ea);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        /* Custom shimmer animation for video loading placeholder */
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </>
  );
}
